import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Stack,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
  Grid,
  Snackbar,
  TextField,
  MenuItem,
  Checkbox,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import {
  createApproval,
  listPatientDocuments,
  listPatients,
  sendApproval,
} from '../../utils/approvalsApi';
import ComposeForm from './components/ComposeForm';
import ComposeSummaryRail from './components/ComposeSummaryRail';
import DocumentAttachWidget from './components/DocumentAttachWidget';
import PhiHintBanner from './components/PhiHintBanner';
import { scanForPhi } from './utils/phiScanner';

const PAGE_SX = { bgcolor: '#fff', minHeight: '100vh' };
const CONTAINER_SX = { py: 4 };
const BREADCRUMB_SX = { mb: 1.5 };
const HEADER_SX = { mb: 3 };
const TITLE_SX = { fontSize: 28, fontWeight: 700, letterSpacing: '-0.01em' };
const SUBTITLE_SX = { fontSize: 14, color: '#64748B', mt: 0.5 };

const SECTION_WRAP_SX = { mb: 4 };
const SECTION_HEADER_SX = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 1.5,
  mb: 1.75,
};
const SECTION_NUM_SX = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 12,
  color: '#64748B',
};
const SECTION_TITLE_SX = { fontSize: 20, fontWeight: 700 };

const PROVIDER_CARD_SX = {
  border: '1px solid #000',
  p: 2,
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  bgcolor: '#fff',
  mb: 2,
};
const PROVIDER_AVATAR_SX = {
  width: 48,
  height: 48,
  borderRadius: '50%',
  bgcolor: '#000',
  color: '#fff',
  fontWeight: 700,
  fontSize: 16,
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0,
};
const PROVIDER_NAME_SX = { fontWeight: 700, fontSize: 16 };
const PROVIDER_META_SX = { fontSize: 13, color: '#64748B', mt: 0.25 };

const READ_ONLY_LABEL_SX = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  color: '#64748B',
};
const READ_ONLY_VALUE_SX = { fontSize: 14, fontWeight: 700, mt: 0.25 };

const HIPAA_BANNER_SX = {
  bgcolor: '#F8F9FC',
  border: '1px solid #000',
  p: '12px 14px',
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  mb: 2,
};
const HIPAA_BANNER_TITLE_SX = { fontSize: 13, fontWeight: 700 };
const HIPAA_BANNER_BODY_SX = { fontSize: 12, color: '#000', lineHeight: 1.5 };

const IDENTITY_ROW_SX = {
  p: '12px 14px',
  border: '1px solid #000',
  bgcolor: '#F8F9FC',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1.5,
  mb: 2,
};
const IDENTITY_CHECKBOX_SX = {
  p: 0.5,
  color: '#000',
  '&.Mui-checked': { color: '#000' },
};
const IDENTITY_TITLE_SX = { fontSize: 13, fontWeight: 700 };
const IDENTITY_BODY_SX = { fontSize: 12, color: '#64748B', mt: 0.25 };

const NPI_MONO_INPUT_SX = {
  fontFamily: 'Roboto Mono, ui-monospace, SFMono-Regular, monospace',
};

const FIELD_STACK_SX = { display: 'flex', flexDirection: 'column', gap: 2 };

const DEFAULT_EMAIL_SUBJECT = 'Please review and sign: appeal packet for your patient';
const DEFAULT_EMAIL_BODY =
  'Hello,\n\nA short appeal packet is ready for your review and signature. ' +
  'Use the secure link below to open the documents and either sign or decline.\n\n' +
  'No patient-identifying information is included in this message; you’ll see the patient context ' +
  'after verifying your identity through the secure link.\n\nThank you.';

const DEFAULT_FORM = {
  patient: '',
  provider_name: '',
  provider_npi: '',
  provider_email: '',
  identity_check_mode: 'npi_last4',
  npi_last4: '',
  expires_in_days: 7,
  email_subject: DEFAULT_EMAIL_SUBJECT,
  email_body: DEFAULT_EMAIL_BODY,
  document_ids: [],
};

const getBindingPatientId = (binding) =>
  binding?.patient?.patient_id || binding?.patient?.id || '';

const getNpiLast4 = (npi) => {
  const digits = String(npi || '').replace(/\D/g, '');
  return digits.length === 10 ? digits.slice(-4) : '';
};

export const buildInitialApprovalForm = (binding) => {
  if (!binding) return DEFAULT_FORM;
  const providerNpi = binding.provider?.npi || '';
  return {
    ...DEFAULT_FORM,
    patient: getBindingPatientId(binding),
    provider_name: binding.provider?.name || '',
    provider_npi: providerNpi,
    npi_last4: getNpiLast4(providerNpi),
  };
};

export const resolveSelectedPatientId = (formPatient, binding) =>
  formPatient || getBindingPatientId(binding);

const EXPIRY_OPTIONS = [3, 7, 14, 30];
const REMIND_OPTIONS = [
  { value: 'off', label: 'Off' },
  { value: '24h', label: '24h' },
  { value: '48h', label: '48h' },
  { value: '72h', label: '72h' },
];

const initialsFromNames = (first, last) => {
  const f = (first || '').trim()[0] || '';
  const l = (last || '').trim()[0] || '';
  const both = `${f}${l}`.toUpperCase();
  return both || '?';
};

const extractFieldErrors = (data) => {
  if (!data || typeof data !== 'object') return {};
  const out = {};
  Object.entries(data).forEach(([k, v]) => {
    if (k === 'error' || k === 'detail') return;
    if (Array.isArray(v)) out[k] = v.join(' ');
    else if (typeof v === 'string') out[k] = v;
  });
  return out;
};

const ComposeApprovalPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const binding = location.state?.binding || null;

  const initialForm = useMemo(() => {
    return buildInitialApprovalForm(binding);
  }, [binding]);

  const [form, setForm] = useState(initialForm);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [topError, setTopError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [requireVerify, setRequireVerify] = useState(true);
  const [autoRemind, setAutoRemind] = useState('off');

  // Fallback (no-binding) path also needs patient search + selection support.
  const [patients, setPatients] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientQuery, setPatientQuery] = useState('');

  const subjectRef = useRef(null);
  const bodyRef = useRef(null);

  const identityCheckMode = requireVerify ? 'npi_last4' : 'magic_link';
  const selectedPatientId = resolveSelectedPatientId(form.patient, binding);
  const bindingPatientId = getBindingPatientId(binding);
  const bindingProviderName = binding?.provider?.name || '';
  const bindingProviderNpi = binding?.provider?.npi || '';

  useEffect(() => {
    if (!binding) return;
    setForm((prev) => {
      const nextPatient = bindingPatientId || prev.patient;
      const patientChanged = Boolean(nextPatient && prev.patient && prev.patient !== nextPatient);
      const nextProviderNpi = bindingProviderNpi || prev.provider_npi;
      const next = {
        ...prev,
        patient: nextPatient,
        provider_name: bindingProviderName || prev.provider_name,
        provider_npi: nextProviderNpi,
        npi_last4: getNpiLast4(nextProviderNpi) || prev.npi_last4,
      };
      if (patientChanged) next.document_ids = [];
      if (
        next.patient === prev.patient &&
        next.provider_name === prev.provider_name &&
        next.provider_npi === prev.provider_npi &&
        next.npi_last4 === prev.npi_last4 &&
        next.document_ids === prev.document_ids
      ) {
        return prev;
      }
      return next;
    });
  }, [binding, bindingPatientId, bindingProviderName, bindingProviderNpi]);

  // Fallback patient search — only runs when there is no binding.
  useEffect(() => {
    if (binding) return undefined;
    const q = patientQuery.trim();
    if (q.length < 2) {
      setPatients((prev) => {
        const selected = prev.find((p) => p.id === form.patient);
        return selected ? [selected] : [];
      });
      setPatientsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setPatientsLoading(true);
    const timer = window.setTimeout(() => {
      listPatients({ search: q })
        .then((res) => {
          if (cancelled) return;
          const data = res?.data;
          const list = Array.isArray(data) ? data : (data?.data || data?.results || []);
          setPatients(list);
        })
        .catch(() => {
          if (!cancelled) setPatients([]);
        })
        .finally(() => {
          if (!cancelled) setPatientsLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientQuery, binding]);

  useEffect(() => {
    if (!selectedPatientId) {
      setDocuments([]);
      return undefined;
    }
    let cancelled = false;
    setDocumentsLoading(true);
    listPatientDocuments(selectedPatientId)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data;
        const list = Array.isArray(data) ? data : (data?.results || []);
        setDocuments(list);
      })
      .catch(() => {
        if (!cancelled) setDocuments([]);
      })
      .finally(() => {
        if (!cancelled) setDocumentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPatientId]);

  const setField = useCallback((name, value) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'provider_npi') {
        const last4 = getNpiLast4(value);
        if (next.identity_check_mode === 'npi_last4' && last4) {
          next.npi_last4 = last4;
        }
      }
      if (name === 'identity_check_mode' && value === 'npi_last4' && !next.npi_last4) {
        next.npi_last4 = getNpiLast4(next.provider_npi);
      }
      if (name === 'patient' && value !== prev.patient) {
        next.document_ids = [];
      }
      return next;
    });
    setFieldErrors((prev) => {
      if (!(name in prev)) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const attachDoc = useCallback((docId) => {
    setForm((prev) => {
      if (prev.document_ids.includes(docId)) return prev;
      return { ...prev, document_ids: [...prev.document_ids, docId] };
    });
  }, []);

  const removeDoc = useCallback((docId) => {
    setForm((prev) => ({
      ...prev,
      document_ids: prev.document_ids.filter((id) => id !== docId),
    }));
  }, []);

  const phiResult = useMemo(() => {
    const firstName = binding?.patient?.first_name
      || (patients.find((p) => p.id === selectedPatientId)?.first_name || '');
    const lastName = binding?.patient?.last_name
      || (patients.find((p) => p.id === selectedPatientId)?.last_name || '');
    return scanForPhi({
      subject: form.email_subject,
      body: form.email_body,
      patientFirstName: firstName,
      patientLastName: lastName,
    });
  }, [form.email_subject, form.email_body, binding, patients, selectedPatientId]);

  const validateLocal = () => {
    const errs = {};
    if (!selectedPatientId) errs.patient = 'Select a patient.';
    if (!form.provider_name?.trim()) errs.provider_name = 'Required.';
    if (!/^\d{10}$/.test(form.provider_npi || '')) errs.provider_npi = 'NPI must be 10 digits.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.provider_email || '')) {
      errs.provider_email = 'Valid email required.';
    }
    if (identityCheckMode === 'npi_last4' && !/^\d{4}$/.test(form.npi_last4 || '')) {
      errs.npi_last4 = 'Last 4 digits required.';
    }
    if (!form.email_subject?.trim()) errs.email_subject = 'Subject required.';
    if (!form.email_body?.trim()) errs.email_body = 'Body required.';
    if (!Number.isInteger(Number(form.expires_in_days)) || form.expires_in_days < 1 || form.expires_in_days > 30) {
      errs.expires_in_days = 'Must be 1–30.';
    }
    if (!form.document_ids?.length) errs.document_ids = 'Attach at least one document.';
    return errs;
  };

  const buildPayload = () => ({
    patient: selectedPatientId,
    provider_name: form.provider_name.trim(),
    provider_npi: form.provider_npi,
    provider_email: form.provider_email.trim(),
    identity_check_mode: identityCheckMode,
    npi_last4: identityCheckMode === 'npi_last4' ? form.npi_last4 : '',
    expires_in_days: Number(form.expires_in_days),
    email_subject: form.email_subject,
    email_body: form.email_body,
    document_ids: form.document_ids,
  });

  const handleSubmit = async (alsoSend) => {
    setTopError(null);
    setFieldErrors({});

    const localErrs = validateLocal();
    if (Object.keys(localErrs).length) {
      setFieldErrors(localErrs);
      setTopError('Please fix the highlighted fields before saving.');
      return;
    }

    setSubmitting(true);
    try {
      const createRes = await createApproval(buildPayload());
      const created = createRes?.data || {};
      const approvalId = created.id;

      if (!alsoSend) {
        setSnackbar({ message: 'Draft saved.', severity: 'success' });
        if (approvalId) {
          navigate(`/approvals/${approvalId}`);
        }
        return;
      }

      try {
        await sendApproval(approvalId);
        setSnackbar({
          message: `Email queued to ${form.provider_email}.`,
          severity: 'success',
        });
        navigate(`/approvals/${approvalId}`);
      } catch (sendErr) {
        const data = sendErr?.response?.data;
        const message = data?.error || data?.detail || 'Send failed. The draft was saved — open it to retry.';
        setTopError(message);
        if (approvalId) {
          navigate(`/approvals/${approvalId}`);
        }
      }
    } catch (err) {
      const data = err?.response?.data;
      const fe = extractFieldErrors(data);
      setFieldErrors(fe);
      const businessMsg = data?.error || data?.detail;
      setTopError(
        businessMsg ||
          (Object.keys(fe).length ? 'Some fields need attention.' : 'Save failed. Try again.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const patientForRail = binding?.patient || patients.find((p) => p.id === selectedPatientId) || null;
  const patientLabel = patientForRail
    ? `${patientForRail.first_name || ''} ${patientForRail.last_name || ''}`.trim()
    : '';

  return (
    <Box sx={PAGE_SX}>
      <Container maxWidth="lg" sx={CONTAINER_SX}>
        <Breadcrumbs sx={BREADCRUMB_SX} aria-label="breadcrumb">
          <MuiLink component={RouterLink} to="/approvals" underline="hover" color="inherit">
            Approvals
          </MuiLink>
          <Typography color="text.primary">New approval</Typography>
        </Breadcrumbs>

        <Box sx={HEADER_SX}>
          <Typography sx={TITLE_SX}>New approval</Typography>
          <Typography sx={SUBTITLE_SX}>
            Compose a PHI-clean note, attach documents, and dispatch a one-time secure link.
          </Typography>
        </Box>

        {topError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTopError(null)}>
            {topError}
          </Alert>
        )}

        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            {/* Section 01 — Recipient */}
            <Box sx={SECTION_WRAP_SX}>
              <Box sx={SECTION_HEADER_SX}>
                <Typography sx={SECTION_NUM_SX}>01</Typography>
                <Typography sx={SECTION_TITLE_SX}>Recipient</Typography>
              </Box>

              {binding ? (
                <Box>
                  <Box sx={PROVIDER_CARD_SX}>
                    <Box sx={PROVIDER_AVATAR_SX}>
                      {initialsFromNames(binding.provider?.first_name, binding.provider?.last_name)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={PROVIDER_NAME_SX} noWrap>
                        {binding.provider?.name || '—'}
                      </Typography>
                      <Typography sx={PROVIDER_META_SX} noWrap>
                        NPI {binding.provider?.npi || '—'}
                        {binding.provider?.taxonomy_code ? ` · ${binding.provider.taxonomy_code}` : ''}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={FIELD_STACK_SX}>
                    <TextField
                      label="Email address"
                      type="email"
                      size="small"
                      fullWidth
                      value={form.provider_email}
                      onChange={(e) => setField('provider_email', e.target.value)}
                      disabled={submitting}
                      error={!!fieldErrors.provider_email}
                      helperText={fieldErrors.provider_email || 'Where the secure link is sent.'}
                    />

                    <Box>
                      <Typography sx={READ_ONLY_LABEL_SX}>NPI</Typography>
                      <Typography sx={READ_ONLY_VALUE_SX}>
                        {binding.provider?.npi || '—'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={READ_ONLY_LABEL_SX}>PATIENT</Typography>
                      <Typography sx={READ_ONLY_VALUE_SX}>
                        {binding.patient?.first_name} {binding.patient?.last_name}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <ComposeForm
                  form={form}
                  setField={setField}
                  patients={patients}
                  patientsLoading={patientsLoading}
                  patientQuery={patientQuery}
                  onPatientQueryChange={setPatientQuery}
                  documents={documents}
                  documentsLoading={documentsLoading}
                  fieldErrors={fieldErrors}
                  phiResult={phiResult}
                  readOnly={submitting}
                  subjectRef={subjectRef}
                  bodyRef={bodyRef}
                />
              )}
            </Box>

            {binding && <Divider sx={{ mb: 4 }} />}

            {/* Section 02 — Documents (only in binding mode; fallback uses ComposeForm's own doc picker) */}
            {binding && (
              <Box sx={SECTION_WRAP_SX}>
                <Box sx={SECTION_HEADER_SX}>
                  <Typography sx={SECTION_NUM_SX}>02</Typography>
                  <Typography sx={SECTION_TITLE_SX}>Documents to approve</Typography>
                </Box>
                <DocumentAttachWidget
                  patientId={selectedPatientId}
                  patientLabel={patientLabel}
                  patientMrn={binding.patient?.mrn}
                  documents={documents}
                  attachedIds={form.document_ids}
                  onAttach={attachDoc}
                  onRemove={removeDoc}
                  loading={documentsLoading}
                  fieldError={fieldErrors.document_ids}
                />
              </Box>
            )}

            {binding && <Divider sx={{ mb: 4 }} />}

            {/* Section 03 — Email */}
            <Box sx={SECTION_WRAP_SX}>
              <Box sx={SECTION_HEADER_SX}>
                <Typography sx={SECTION_NUM_SX}>03</Typography>
                <Typography sx={SECTION_TITLE_SX}>Email</Typography>
              </Box>

              <Box sx={HIPAA_BANNER_SX}>
                <Typography sx={HIPAA_BANNER_TITLE_SX}>
                  HIPAA — no PHI in this email.
                </Typography>
                <Typography sx={HIPAA_BANNER_BODY_SX}>
                  Patient name, claim number, and payer are only visible after the
                  provider authenticates on the portal. Document type names are OK.
                </Typography>
              </Box>

              <Box sx={FIELD_STACK_SX}>
                <TextField
                  label="Subject"
                  size="small"
                  fullWidth
                  value={form.email_subject}
                  onChange={(e) => setField('email_subject', e.target.value)}
                  disabled={submitting}
                  error={!!fieldErrors.email_subject}
                  helperText={fieldErrors.email_subject || ' '}
                  inputRef={subjectRef}
                />
                <TextField
                  label="Body"
                  multiline
                  minRows={8}
                  size="small"
                  fullWidth
                  value={form.email_body}
                  onChange={(e) => setField('email_body', e.target.value)}
                  disabled={submitting}
                  error={!!fieldErrors.email_body}
                  helperText={fieldErrors.email_body || 'Keep this PHI-free. The secure link is appended server-side.'}
                  inputRef={bodyRef}
                />
                {phiResult?.flagged && <PhiHintBanner hits={phiResult.hits} />}
              </Box>
            </Box>

            <Divider sx={{ mb: 4 }} />

            {/* Section 04 — Security & expiry */}
            <Box sx={SECTION_WRAP_SX}>
              <Box sx={SECTION_HEADER_SX}>
                <Typography sx={SECTION_NUM_SX}>04</Typography>
                <Typography sx={SECTION_TITLE_SX}>Security & expiry</Typography>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <TextField
                  select
                  label="Link expires in"
                  size="small"
                  fullWidth
                  value={form.expires_in_days}
                  onChange={(e) => setField('expires_in_days', Number(e.target.value))}
                  disabled={submitting}
                  error={!!fieldErrors.expires_in_days}
                  helperText={fieldErrors.expires_in_days || ' '}
                >
                  {EXPIRY_OPTIONS.map((days) => (
                    <MenuItem key={days} value={days}>
                      {days} days
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Auto-remind if unread"
                  size="small"
                  fullWidth
                  value={autoRemind}
                  onChange={(e) => setAutoRemind(e.target.value)}
                  disabled={submitting}
                  helperText=" "
                >
                  {/* TODO: pass autoRemind to backend when supported */}
                  {REMIND_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              <Box sx={IDENTITY_ROW_SX}>
                <Checkbox
                  checked={requireVerify}
                  onChange={(e) => setRequireVerify(e.target.checked)}
                  size="small"
                  sx={IDENTITY_CHECKBOX_SX}
                  disabled={submitting}
                />
                <Box>
                  <Typography sx={IDENTITY_TITLE_SX}>
                    Require identity verification
                  </Typography>
                  <Typography sx={IDENTITY_BODY_SX}>
                    Provider must enter the last 4 of their NPI before viewing documents.
                  </Typography>
                </Box>
              </Box>

              {requireVerify && (
                <TextField
                  label="NPI last 4"
                  size="small"
                  value={form.npi_last4}
                  onChange={(e) => setField('npi_last4', e.target.value.replace(/\D/g, '').slice(0, 4))}
                  disabled={submitting}
                  error={!!fieldErrors.npi_last4}
                  helperText={fieldErrors.npi_last4 || 'Auto-filled from the provider NPI.'}
                  inputProps={{ inputMode: 'numeric', pattern: '\\d{4}', style: NPI_MONO_INPUT_SX }}
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <ComposeSummaryRail
              patient={patientForRail}
              providerEmail={form.provider_email}
              documentsCount={form.document_ids.length}
              expiresInDays={form.expires_in_days}
              submitting={submitting}
              phiDetected={!!phiResult?.flagged}
              onCancel={() => navigate(-1)}
              onSaveDraft={() => handleSubmit(false)}
              onSend={() => handleSubmit(true)}
            />
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} variant="filled">
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default ComposeApprovalPage;
